import {
  Box,
  Button,
  Divider,
  Icons,
  MenuItem,
  Radio,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Popover,
} from '@ublend-npm/aulaui-core';
import React, { useMemo, useState } from 'react';
import { hashHistory } from 'react-router';
import useIsOverflowing from '@app/hooks/useIsOverflowing';
import { createStyles } from './GroupFilter.styles';

export const MANAGE_GROUPS_VALUE = 'manage-groups';
export const CLEAR_GROUP_FILTER_VALUE = 'footer-actions';
const MAX_DROPDOWN_HEIGHT = 320;

const getCurrentRoute = () => {
  const { pathname, query } = hashHistory.getCurrentLocation();
  const params = new URLSearchParams();

  Object.entries(query || {}).forEach(([key, value]) => {
    if (value != null) {
      params.set(key, String(value));
    }
  });

  const queryString = params.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
};

export type GroupFilterSet = {
  externalId?: string;
  isHeader?: boolean;
  key: string;
  label: string;
};

type GroupFilterProps = {
  groups: GroupFilterSet[];
  onClear: () => void;
  onManageGroups: (queryParams: {
    from: 'assignments';
    returnTo: string;
  }) => void;
  onSelectGroup: (groupId: string) => void;
  value: string;
};

const GroupLabel = ({
  children,
  sx,
}: {
  children: string;
  sx?: Record<string, unknown>;
}) => {
  const [overflowRef, isOverflowing] = useIsOverflowing<HTMLSpanElement>();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const label = (
    <Box
      component="span"
      ref={overflowRef}
      sx={{
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        ...sx,
      }}
    >
      {children}
    </Box>
  );

  if (!isMdUp) {
    return label;
  }

  return (
    <Tooltip
      arrow
      disableFocusListener={!isOverflowing}
      disableHoverListener={!isOverflowing}
      disableTouchListener={!isOverflowing}
      position="top"
      title={children}
    >
      {label}
    </Tooltip>
  );
};

const GroupFilter = ({
  groups,
  onClear,
  onManageGroups,
  onSelectGroup,
  value,
}: GroupFilterProps) => {
  const theme = useTheme();
  const isDark = theme?.palette?.mode === 'dark';
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [menuMaxHeight, setMenuMaxHeight] = useState(480);
  const [pendingValue, setPendingValue] = useState(value);
  const groupSets = useMemo(() => {
    const seen = new Set<string>();
    const sets: Array<{
      key: string;
      label: string;
      groups: GroupFilterSet[];
    }> = [];
    let currentSet: typeof sets[number] | undefined;

    groups.forEach(({ externalId, isHeader, key, label }) => {
      if (
        externalId ||
        key === MANAGE_GROUPS_VALUE ||
        key === CLEAR_GROUP_FILTER_VALUE
      ) {
        return;
      }
      if (isHeader) {
        currentSet = { key, label, groups: [] };
        sets.push(currentSet);
        return;
      }
      if (seen.has(key)) return false;
      seen.add(key);
      currentSet?.groups.push({ isHeader: false, key, label });
    });

    // Match production: group sets without groups are not filter options.
    return sets.filter(({ groups }) => groups.length > 0);
  }, [groups]);
  const options = groupSets.flatMap(({ groups }) => groups);

  const selectedLabel =
    options.find(({ key }) => key === value)?.label || 'Filter by group';
  const closeMenu = () => {
    setAnchorEl(null);
    setPendingValue(value);
  };
  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    const { bottom } = event.currentTarget.getBoundingClientRect();
    // Keep the list below its trigger. Overflowing groups scroll within the menu
    // instead of causing MUI to reposition the menu over the trigger.
    setMenuMaxHeight(Math.max(160, window.innerHeight - bottom - 16));
    setPendingValue(value);
    setAnchorEl(event.currentTarget);
  };
  const applyFilter = () => {
    const nextValue = pendingValue;
    closeMenu();
    if (nextValue === value) return;
    if (!nextValue) {
      onClear();
      return;
    }
    onSelectGroup(nextValue);
  };
  const dropdownMaxHeight = Math.min(MAX_DROPDOWN_HEIGHT, menuMaxHeight);
  const styles = createStyles(theme, isDark, dropdownMaxHeight);

  return (
    <>
      <Button
        aria-label="Filter submissions by group"
        aria-controls={anchorEl ? 'group-filter-menu' : undefined}
        aria-expanded={Boolean(anchorEl)}
        aria-haspopup="dialog"
        endIcon={
          anchorEl ? <Icons.KeyboardArrowUp /> : <Icons.KeyboardArrowDown />
        }
        kind="secondary"
        onClick={openMenu}
        sx={styles.filterButton}
      >
        <GroupLabel sx={styles.selectedLabel}>{selectedLabel}</GroupLabel>
      </Button>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        id="group-filter-menu"
        onClose={closeMenu}
        open={Boolean(anchorEl)}
        PaperProps={{
          'aria-label': 'Filter submissions by group',
          role: 'dialog',
          sx: styles.menuPaper,
          style: styles.menuPaperStyle,
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      >
        <Box sx={styles.scrollableContent}>
          <Box
            aria-label="Group filter options"
            role="radiogroup"
            sx={styles.groupList}
          >
            {groupSets.map(
              ({ groups: setGroups, key: setKey, label }, index) => (
                <React.Fragment key={setKey}>
                  {index > 0 && <Divider />}
                  <Box sx={styles.groupSet}>
                    <GroupLabel sx={styles.groupSetText}>{label}</GroupLabel>
                  </Box>
                  {setGroups.map(({ key, label: groupLabel }) => (
                    <MenuItem
                      className="group-filter-option"
                      key={key}
                      autoFocus={
                        key === value || (!value && key === options[0]?.key)
                      }
                      aria-checked={key === pendingValue}
                      label={
                        <Box
                          alignItems="center"
                          display="flex"
                          sx={styles.groupOptionLabel}
                        >
                          <Radio
                            checked={key === pendingValue}
                            inputProps={{ 'aria-hidden': true }}
                            onChange={() => {}}
                            sx={styles.radio}
                            tabIndex={-1}
                            value={key}
                          />
                          <GroupLabel sx={styles.groupOptionText}>
                            {groupLabel}
                          </GroupLabel>
                        </Box>
                      }
                      onClick={() => setPendingValue(key)}
                      role="radio"
                      selected={key === pendingValue}
                      tabIndex={0}
                    />
                  ))}
                </React.Fragment>
              )
            )}
          </Box>
          <Divider />
          <Box sx={styles.manageGroupsContainer}>
            <Box sx={styles.manageGroupsButton}>
              <MenuItem
                icon={
                  <Box sx={styles.manageGroupsIconWrapper}>
                    <Icons.Settings fontSize="small" />
                  </Box>
                }
                label={
                  <Box sx={styles.manageGroupsLabel}>
                    <Typography sx={styles.manageText}>
                      Manage groups
                    </Typography>
                    <Icons.ChevronRight
                      aria-hidden
                      fontSize="small"
                      sx={styles.manageGroupsArrow}
                    />
                  </Box>
                }
                onClick={() => {
                  closeMenu();
                  onManageGroups({
                    from: 'assignments',
                    returnTo: getCurrentRoute(),
                  });
                }}
                role="button"
                tabIndex={0}
              />
            </Box>
          </Box>
        </Box>
        <Divider />
        <Box sx={styles.footer}>
          <Button kind="secondary" onClick={() => setPendingValue('')}>
            Clear
          </Button>
          <Button kind="primary" onClick={applyFilter}>
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default GroupFilter;
